/**
 * StatCards - Gamification stat cards for the vault dashboard
 *
 * Features:
 * - Streak display with fire animation
 * - Win rate with progress ring
 * - ATH card with crown
 * - Milestone progress
 * - Smooth animations
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useVault } from '../../contexts/VaultContext';

interface StatCardsProps {
  className?: string;
}

const StatCards: React.FC<StatCardsProps> = ({ className = '' }) => {
  const { streaks, ath, milestones, isLoading } = useVault();

  if (isLoading) {
    return (
      <div className={`${className} grid grid-cols-2 gap-3`}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-gray-800 rounded-xl p-4 h-28 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className={`${className} grid grid-cols-2 gap-3`}>
      <StreakCard
        type={streaks?.current.type || 'none'}
        count={streaks?.current.count || 0}
        bestWinning={streaks?.best_winning.count || 0}
      />
      <WinRateCard
        winRate={streaks?.stats.win_rate || 0}
        winningDays={streaks?.stats.winning_days || 0}
        losingDays={streaks?.stats.losing_days || 0}
      />
      <ATHCard
        value={ath?.value || 0}
        isAtATH={ath?.is_at_ath || false}
        timesBroken={ath?.times_broken || 0}
      />
      <MilestoneCard
        achieved={milestones.total}
        next={milestones.next}
      />
    </div>
  );
};

// Streak Card
interface StreakCardProps {
  type: 'winning' | 'losing' | 'none';
  count: number;
  bestWinning: number;
}

const StreakCard: React.FC<StreakCardProps> = ({ type, count, bestWinning }) => {
  const isWinning = type === 'winning';
  const isLosing = type === 'losing';

  const getEmoji = () => {
    if (isWinning) return '🔥';
    if (isLosing) return '❄️';
    return '➖';
  };

  const getLabel = () => {
    if (isWinning) return 'Day Winning Streak';
    if (isLosing) return 'Day Losing Streak';
    return 'No Active Streak';
  };

  const getBgClass = () => {
    if (isWinning) return 'from-orange-500/10 to-red-500/10 border-orange-500/20';
    if (isLosing) return 'from-blue-500/10 to-cyan-500/10 border-blue-500/20';
    return 'from-gray-700/50 to-gray-800/50 border-gray-600/20';
  };

  return (
    <motion.div
      className={`bg-gradient-to-br ${getBgClass()} border rounded-xl p-4 relative overflow-hidden`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      {/* Animated fire background for winning streaks */}
      {isWinning && count >= 3 && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-orange-500/20 to-transparent"
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <motion.span
            className="text-2xl"
            animate={isWinning ? {
              scale: [1, 1.2, 1],
              rotate: [0, 5, -5, 0],
            } : {}}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
          >
            {getEmoji()}
          </motion.span>
          <span className="text-xs text-gray-500">Best: {bestWinning}</span>
        </div>

        <motion.p
          className={`text-3xl font-bold ${isWinning ? 'text-orange-400' : isLosing ? 'text-blue-400' : 'text-gray-400'}`}
          key={count}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
        >
          {count}
        </motion.p>

        <p className="text-xs text-gray-400 mt-1">{getLabel()}</p>
      </div>
    </motion.div>
  );
};

// Win Rate Card
interface WinRateCardProps {
  winRate: number;
  winningDays: number;
  losingDays: number;
}

const WinRateCard: React.FC<WinRateCardProps> = ({ winRate, winningDays, losingDays }) => {
  const circumference = 2 * Math.PI * 20; // radius = 20
  const strokeDashoffset = circumference - (winRate / 100) * circumference;

  const getColor = () => {
    if (winRate >= 60) return '#22c55e';
    if (winRate >= 50) return '#eab308';
    return '#ef4444';
  };

  return (
    <motion.div
      className="bg-gradient-to-br from-gray-700/50 to-gray-800/50 border border-gray-600/20 rounded-xl p-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <div className="flex items-center gap-3">
        {/* Progress ring */}
        <div className="relative w-12 h-12">
          <svg className="w-12 h-12 -rotate-90">
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke="#374151"
              strokeWidth="4"
            />
            <motion.circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              stroke={getColor()}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">
            {winRate.toFixed(0)}%
          </span>
        </div>

        <div>
          <p className="text-sm font-medium text-white">Win Rate</p>
          <p className="text-xs text-gray-400">
            {winningDays}W / {losingDays}L
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// ATH Card
interface ATHCardProps {
  value: number;
  isAtATH: boolean;
  timesBroken: number;
}

const ATHCard: React.FC<ATHCardProps> = ({ value, isAtATH, timesBroken }) => {
  const formatCurrency = (val: number) => {
    if (val >= 1000000) {
      return `$${(val / 1000000).toFixed(2)}M`;
    }
    if (val >= 1000) {
      return `$${(val / 1000).toFixed(1)}K`;
    }
    return `$${val.toFixed(0)}`;
  };

  return (
    <motion.div
      className={`bg-gradient-to-br ${
        isAtATH
          ? 'from-yellow-500/20 to-amber-500/10 border-yellow-500/30'
          : 'from-gray-700/50 to-gray-800/50 border-gray-600/20'
      } border rounded-xl p-4 relative overflow-hidden`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      {/* Glow effect when at ATH */}
      {isAtATH && (
        <motion.div
          className="absolute inset-0 bg-yellow-500/10"
          animate={{
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <motion.span
            className="text-2xl"
            animate={isAtATH ? {
              y: [0, -5, 0],
              scale: [1, 1.1, 1],
            } : {}}
            transition={{ duration: 1, repeat: Infinity }}
          >
            👑
          </motion.span>
          {isAtATH && (
            <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">
              AT ATH!
            </span>
          )}
        </div>

        <p className={`text-2xl font-bold ${isAtATH ? 'text-yellow-400' : 'text-white'}`}>
          {formatCurrency(value)}
        </p>

        <p className="text-xs text-gray-400 mt-1">
          All-Time High · {timesBroken}x broken
        </p>
      </div>
    </motion.div>
  );
};

// Milestone Card
interface MilestoneCardProps {
  achieved: number;
  next: {
    id: number;
    name: string;
    value: number;
    icon: string;
    progress_percent: number;
  } | null;
}

const MilestoneCard: React.FC<MilestoneCardProps> = ({ achieved, next }) => {
  return (
    <motion.div
      className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/20 rounded-xl p-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">🏅</span>
        <span className="text-xs text-gray-400">{achieved} achieved</span>
      </div>

      {next ? (
        <>
          <p className="text-sm font-medium text-white truncate">{next.name}</p>
          <div className="mt-2">
            <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500"
                initial={{ width: 0 }}
                animate={{ width: `${next.progress_percent}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">{next.progress_percent.toFixed(0)}% complete</p>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-400">All milestones achieved!</p>
      )}
    </motion.div>
  );
};

export default StatCards;
